import React, { useState } from 'react';
import {
    Input,
    TextArea,
    Dropdown,
    Button,
    alert,
    AlertType,
} from '@quyva/shared-ui';
import {
    faPaperPlane,
    faXmark,
    faClipboardList,
    faAngleLeft,
    faAngleRight,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// In the detailed (panel) view, fields are paginated this many per page.
const FIELDS_PER_PAGE = 5;

// Initial value for a field, honoring its type and default_value.
const initialValue = (field) => {
    if (field.type === 'integer') {
        return field.default_value ?? 0;
    }
    if (field.type === 'select') {
        return '';
    }
    return field.default_value ?? '';
};

/**
 * Renders an interactive form from a form_input config ({use_detailed_view, fields})
 * and lets the user submit or cancel it. Field types: text (single/multi line),
 * integer, select. Used both inline in chat and inside the document panel.
 */
const FormRenderer = ({ formConfig, onSubmit, onCancel, isPanel = false }) => {
    const fields = Array.isArray(formConfig?.fields) ? formConfig.fields : [];

    const [values, setValues] = useState(() => {
        const init = {};
        fields.forEach((field, index) => {
            init[index] = initialValue(field);
        });
        return init;
    });
    const [page, setPage] = useState(0);

    // Paginate only in the detailed (panel) view when there are enough fields.
    const paginated = isPanel && fields.length > FIELDS_PER_PAGE;
    const pageCount = paginated
        ? Math.ceil(fields.length / FIELDS_PER_PAGE)
        : 1;
    const startIndex = paginated ? page * FIELDS_PER_PAGE : 0;
    const endIndex = paginated ? startIndex + FIELDS_PER_PAGE : fields.length;
    const visibleIndices = fields
        .map((_, index) => index)
        .slice(startIndex, endIndex);

    const setValue = (index, value) => {
        setValues((prev) => ({ ...prev, [index]: value }));
    };

    const labelFor = (field, index) => field.label || `Field ${index + 1}`;

    const handleSubmit = () => {
        // Collect every missing required field so the user sees them all at once.
        const missing = [];
        fields.forEach((field, index) => {
            const value = values[index];
            if (
                field.is_required &&
                (value === '' || value === null || value === undefined)
            ) {
                missing.push({ label: labelFor(field, index), index });
            }
        });

        if (missing.length > 0) {
            // Jump to the page holding the first missing field so it's visible.
            if (paginated) {
                setPage(Math.floor(missing[0].index / FIELDS_PER_PAGE));
            }
            const labels = missing.map((item) => `"${item.label}"`).join(', ');
            alert(
                AlertType.ERROR,
                `Please fill in the required field${
                    missing.length > 1 ? 's' : ''
                }: ${labels}.`
            );
            return;
        }

        const answers = {};
        fields.forEach((field, index) => {
            answers[labelFor(field, index)] = values[index];
        });
        onSubmit(answers);
    };

    const renderField = (field, index) => {
        const value = values[index];

        if (field.type === 'select') {
            const options = (field.select_options || []).map((opt) => ({
                key: opt,
                label: opt,
                action: () => setValue(index, opt),
                selected: value === opt,
            }));
            return (
                <Dropdown
                    dropDownList={options}
                    showButtonText
                    buttonText={value || 'Select an option'}
                    placement='bottomLeft'
                    buttonClassName='w-full border px-3 py-2 text-base justify-between'
                    listClassName='min-w-[200px]'
                />
            );
        }

        if (field.type === 'integer') {
            return (
                <Input
                    type='number'
                    value={value}
                    placeholder='0'
                    onChange={(e) => {
                        const parsed = parseInt(e.target.value, 10);
                        setValue(index, Number.isNaN(parsed) ? '' : parsed);
                    }}
                />
            );
        }

        // text
        if (field.is_big_text) {
            return (
                <TextArea
                    value={value}
                    placeholder={labelFor(field, index)}
                    onChange={(e) => setValue(index, e.target.value)}
                    rows={4}
                    className='min-h-[80px] resize-none p-2 focus:outline-backgrounds-primary200 border rounded-xl'
                />
            );
        }
        return (
            <Input
                value={value}
                placeholder={labelFor(field, index)}
                onChange={(e) => setValue(index, e.target.value)}
            />
        );
    };

    return (
        <div
            className={`w-full flex flex-col rounded-xl border border-backgrounds-border bg-backgrounds-white text-texts-secondary300 ${
                isPanel ? 'p-5 h-full' : 'p-4 mt-2 max-h-[65vh]'
            }`}
        >
            <div className='shrink-0 flex items-center pb-2 border-b border-backgrounds-border'>
                <FontAwesomeIcon
                    icon={faClipboardList}
                    className='mr-2 text-icons-primary drop-shadow-sm'
                />
                <span className='font-semibold text-base'>
                    Please fill in the details
                </span>
            </div>

            {/* Scrollable fields area; header and footer stay fixed. */}
            <div className='flex-1 min-h-0 overflow-auto flex flex-col gap-3 py-3'>
                {fields.length === 0 ? (
                    <div className='text-sm text-texts-secondary200 italic'>
                        This form has no fields to fill.
                    </div>
                ) : (
                    visibleIndices.map((index) => {
                        const field = fields[index];
                        return (
                            <div key={index} className='flex flex-col gap-1'>
                                <label className='text-sm font-medium text-texts-secondary300'>
                                    {labelFor(field, index)}
                                    {field.is_required && (
                                        <span className='text-texts-red400'>
                                            {' '}
                                            *
                                        </span>
                                    )}
                                </label>
                                {renderField(field, index)}
                            </div>
                        );
                    })
                )}
            </div>

            {/* Footer (fixed at bottom): pagination on the left, actions on the right */}
            <div className='shrink-0 flex items-center justify-between gap-2 pt-3 border-t border-backgrounds-border'>
                <div className='flex items-center gap-2'>
                    {paginated && (
                        <>
                            <Button
                                icon={faAngleLeft}
                                type='warning'
                                hoverText='Previous page'
                                disabled={page === 0}
                                onClick={() =>
                                    setPage((prev) => Math.max(0, prev - 1))
                                }
                            />
                            <span className='text-sm text-texts-secondary300 select-none'>
                                Page {page + 1} of {pageCount}
                            </span>
                            <Button
                                icon={faAngleRight}
                                type='warning'
                                hoverText='Next page'
                                disabled={page >= pageCount - 1}
                                onClick={() =>
                                    setPage((prev) =>
                                        Math.min(pageCount - 1, prev + 1)
                                    )
                                }
                            />
                        </>
                    )}
                </div>
                <div className='flex items-center gap-2'>
                    <Button
                        type='danger'
                        icon={faXmark}
                        text='Cancel'
                        hoverText='Cancel the form and unblock chat'
                        onClick={onCancel}
                    />
                    <Button
                        type='success'
                        icon={faPaperPlane}
                        text='Submit'
                        hoverText='Submit the form'
                        onClick={handleSubmit}
                        disabled={fields.length === 0}
                    />
                </div>
            </div>
        </div>
    );
};

export default FormRenderer;
